export class SoundManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private ambienceGain: GainNode | null = null;

  private isMuted = false;
  private masterVol = 0.8;
  private sfxVol = 0.8;
  private ambVol = 0.4;

  private isAmbiencePlaying = false;
  private ambienceNodes: { stop: () => void }[] = [];

  constructor() {
    // AudioContext will be initialized on first user interaction
  }

  private initContext(): void {
    if (this.ctx) return;
    try {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AudioCtx();

      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.isMuted ? 0 : this.masterVol;
      this.masterGain.connect(this.ctx.destination);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = this.sfxVol;
      this.sfxGain.connect(this.masterGain);

      this.ambienceGain = this.ctx.createGain();
      this.ambienceGain.gain.value = this.ambVol;
      this.ambienceGain.connect(this.masterGain);
    } catch (e) {
      console.warn('Web Audio not supported or blocked:', e);
    }
  }

  public resume(): void {
    this.initContext();
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  public setMasterVolume(val: number): void {
    this.masterVol = Math.max(0, Math.min(1, val));
    if (this.masterGain && !this.isMuted) {
      this.masterGain.gain.setTargetAtTime(this.masterVol, this.ctx?.currentTime || 0, 0.05);
    }
  }

  public setSfxVolume(val: number): void {
    this.sfxVol = Math.max(0, Math.min(1, val));
    if (this.sfxGain) {
      this.sfxGain.gain.setTargetAtTime(this.sfxVol, this.ctx?.currentTime || 0, 0.05);
    }
  }

  public setAmbienceVolume(val: number): void {
    this.ambVol = Math.max(0, Math.min(1, val));
    if (this.ambienceGain) {
      this.ambienceGain.gain.setTargetAtTime(this.ambVol, this.ctx?.currentTime || 0, 0.05);
    }
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    if (this.masterGain) {
      this.masterGain.gain.setTargetAtTime(this.isMuted ? 0 : this.masterVol, this.ctx?.currentTime || 0, 0.05);
    }
    return this.isMuted;
  }

  public get muted(): boolean {
    return this.isMuted;
  }

  // --- Sound Effects Synthesizers ---

  // Stone piece sliding on the board
  public playStoneSlide(): void {
    this.resume();
    if (!this.ctx || !this.sfxGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    // Noise buffer for stone friction
    const bufferSize = this.ctx.sampleRate * 0.35;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.6;
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(450, t);
    filter.frequency.exponentialRampToValueAtTime(180, t + 0.35);
    filter.Q.value = 3.5;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.01, t);
    gain.gain.linearRampToValueAtTime(0.35, t + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain);

    noise.start(t);
    noise.stop(t + 0.36);
  }

  // Solid stone impact / placement thud
  public playStonePlace(): void {
    this.resume();
    if (!this.ctx || !this.sfxGain || this.isMuted) return;

    const t = this.ctx.currentTime;

    // Deep resonant stone thump
    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(140, t);
    osc.frequency.exponentialRampToValueAtTime(45, t + 0.18);

    const oscGain = this.ctx.createGain();
    oscGain.gain.setValueAtTime(0.8, t);
    oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);

    osc.connect(oscGain);
    oscGain.connect(this.sfxGain);

    // Stone click transient
    const clickOsc = this.ctx.createOscillator();
    clickOsc.type = 'sine';
    clickOsc.frequency.setValueAtTime(750, t);
    clickOsc.frequency.exponentialRampToValueAtTime(220, t + 0.05);

    const clickGain = this.ctx.createGain();
    clickGain.gain.setValueAtTime(0.5, t);
    clickGain.gain.exponentialRampToValueAtTime(0.001, t + 0.06);

    clickOsc.connect(clickGain);
    clickGain.connect(this.sfxGain);

    osc.start(t);
    osc.stop(t + 0.23);
    clickOsc.start(t);
    clickOsc.stop(t + 0.07);
  }

  // Knight gallop / hop sound
  public playKnightHop(): void {
    this.resume();
    if (!this.ctx || !this.sfxGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(220, t);
    osc.frequency.exponentialRampToValueAtTime(480, t + 0.15);
    osc.frequency.exponentialRampToValueAtTime(160, t + 0.3);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.01, t);
    gain.gain.linearRampToValueAtTime(0.3, t + 0.12);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);

    osc.connect(gain);
    gain.connect(this.sfxGain);

    osc.start(t);
    osc.stop(t + 0.36);
  }

  // Magical spell glide (Bishop, Queen)
  public playSpellGlide(): void {
    this.resume();
    if (!this.ctx || !this.sfxGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    osc1.type = 'sine';
    osc2.type = 'sine';

    osc1.frequency.setValueAtTime(520, t);
    osc1.frequency.exponentialRampToValueAtTime(880, t + 0.3);

    osc2.frequency.setValueAtTime(660, t);
    osc2.frequency.exponentialRampToValueAtTime(1040, t + 0.3);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.01, t);
    gain.gain.linearRampToValueAtTime(0.2, t + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(this.sfxGain);

    osc1.start(t);
    osc1.stop(t + 0.42);
    osc2.start(t);
    osc2.stop(t + 0.42);
  }

  // Dramatic combat strike and stone destruction / shattering
  public playCaptureShatter(): void {
    this.resume();
    if (!this.ctx || !this.sfxGain || this.isMuted) return;

    const t = this.ctx.currentTime;

    // 1. Heavy low impact boom
    const boom = this.ctx.createOscillator();
    boom.type = 'sine';
    boom.frequency.setValueAtTime(180, t);
    boom.frequency.exponentialRampToValueAtTime(35, t + 0.4);

    const boomGain = this.ctx.createGain();
    boomGain.gain.setValueAtTime(1.0, t);
    boomGain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);

    boom.connect(boomGain);
    boomGain.connect(this.sfxGain);
    boom.start(t);
    boom.stop(t + 0.46);

    // 2. High noise crunch / stone shatter
    const bufferSize = this.ctx.sampleRate * 0.6;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (this.ctx.sampleRate * 0.15));
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(1200, t);
    filter.frequency.linearRampToValueAtTime(400, t + 0.5);

    const crunchGain = this.ctx.createGain();
    crunchGain.gain.setValueAtTime(0.8, t);
    crunchGain.gain.exponentialRampToValueAtTime(0.001, t + 0.6);

    noise.connect(filter);
    filter.connect(crunchGain);
    crunchGain.connect(this.sfxGain);

    noise.start(t);
    noise.stop(t + 0.62);

    // 3. Magical arcane chime on kill
    const magic = this.ctx.createOscillator();
    magic.type = 'triangle';
    magic.frequency.setValueAtTime(980, t + 0.05);
    magic.frequency.exponentialRampToValueAtTime(240, t + 0.5);

    const magicGain = this.ctx.createGain();
    magicGain.gain.setValueAtTime(0.3, t + 0.05);
    magicGain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);

    magic.connect(magicGain);
    magicGain.connect(this.sfxGain);
    magic.start(t + 0.05);
    magic.stop(t + 0.52);
  }

  // Check warning cue: dark resonant ominous brass chord
  public playCheck(): void {
    this.resume();
    if (!this.ctx || !this.sfxGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const freqs = [185, 233, 277]; // Low ominous minor chord

    freqs.forEach((freq) => {
      const osc = this.ctx!.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, t);

      const filter = this.ctx!.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(600, t);
      filter.frequency.exponentialRampToValueAtTime(150, t + 0.8);

      const gain = this.ctx!.createGain();
      gain.gain.setValueAtTime(0.25, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.85);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain!);

      osc.start(t);
      osc.stop(t + 0.9);
    });
  }

  // Checkmate / Victory fanfare
  public playVictory(): void {
    this.resume();
    if (!this.ctx || !this.sfxGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const notes = [
      { f: 261.63, time: 0, dur: 0.18 }, // C4
      { f: 329.63, time: 0.16, dur: 0.18 }, // E4
      { f: 392.00, time: 0.32, dur: 0.22 }, // G4
      { f: 523.25, time: 0.52, dur: 0.8 }   // C5
    ];

    notes.forEach(({ f, time, dur }) => {
      const osc = this.ctx!.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(f, t + time);

      const gain = this.ctx!.createGain();
      gain.gain.setValueAtTime(0.4, t + time);
      gain.gain.exponentialRampToValueAtTime(0.001, t + time + dur);

      osc.connect(gain);
      gain.connect(this.sfxGain!);

      osc.start(t + time);
      osc.stop(t + time + dur + 0.05);
    });
  }

  // Button click interaction
  public playButtonClick(): void {
    this.resume();
    if (!this.ctx || !this.sfxGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, t);
    osc.frequency.exponentialRampToValueAtTime(400, t + 0.04);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.045);

    osc.connect(gain);
    gain.connect(this.sfxGain);

    osc.start(t);
    osc.stop(t + 0.05);
  }

  // Ambient castle sound generator (low cavernous wind + torch embers)
  public startCastleAmbience(): void {
    if (this.isAmbiencePlaying) return;
    this.resume();
    if (!this.ctx || !this.ambienceGain) return;

    try {
      // 1. Deep castle rumble / wind
      const bufferSize = this.ctx.sampleRate * 3;
      const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * 0.4;
      }

      const noise = this.ctx.createBufferSource();
      noise.buffer = buffer;
      noise.loop = true;

      const filter = this.ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 120; // Deep sub-bass castle ambiance

      noise.connect(filter);
      filter.connect(this.ambienceGain);
      noise.start();

      this.ambienceNodes.push({
        stop: () => {
          try {
            noise.stop();
            noise.disconnect();
          } catch {}
        }
      });

      this.isAmbiencePlaying = true;
    } catch (e) {
      console.warn('Failed to start ambient audio:', e);
    }
  }

  public stopCastleAmbience(): void {
    this.ambienceNodes.forEach((n) => n.stop());
    this.ambienceNodes = [];
    this.isAmbiencePlaying = false;
  }
}
